import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { createColumnHelper } from "@tanstack/react-table";
import moment from "moment-timezone";
import { Info } from "lucide-react";
import { useUser } from "../../../utils/hooks/user";
import { useCompanyDateFormat, useCompanyTimeFormat } from "../../../utils/hooks/company";
import { getPrimaryActiveResource } from "../../../utils/helpers/user";
import { firstToUpper } from "../../../utils/helpers/string";
import { renderDurationAsFormat } from "../../../utils/helpers/date";

export interface BulkJob {
    type: string;
    createdAt: string;
    completedAt: string;
    completed: number;
    failed: number;
    totalTime: number;
    status: string;
    result?: unknown;
}

interface OpenPayload {
    type: string;
    data: unknown;
}

interface UseColumnsProps {
    open: (payload: OpenPayload) => void;
}

const columnHelper = createColumnHelper<BulkJob>();

export const useColumns = ({ open }: UseColumnsProps) => {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const timeFormat = useCompanyTimeFormat();
    const user = useUser();

    const primaryLocation = useMemo(
        () => getPrimaryActiveResource(user?.locations || []),
        [user?.locations]
    );

    return useMemo(() => {
        const columns = [
            columnHelper.accessor("type", {
                enableSorting: false,
                header: t("type"),
                cell: (info) => {
                    const type = info.getValue();
                    return type && <div className="tw-uppercase">{firstToUpper(t(type))}</div>
                }
            }),
            columnHelper.accessor("createdAt", {
                enableSorting: false,
                header: t("created-at"),
                cell: (info) => {
                    const createdAt = info.getValue();
                    const timezone = primaryLocation?.timezone || "UTC";

                    return createdAt && (
                        <>
                            {moment(createdAt).tz(timezone).format(dateFormat)} -{" "}
                            {moment(createdAt).tz(timezone).format(timeFormat)}
                        </>
                    );
                },
            }),
            columnHelper.accessor("completedAt", {
                enableSorting: false,
                header: t("completed-at"),
                cell: (info) => {
                    const completedAt = info.getValue();
                    const timezone = primaryLocation?.timezone || "UTC";
                    return completedAt && (
                        <>
                            {moment(completedAt).tz(timezone).format(dateFormat)} -{" "}
                            {moment(completedAt).tz(timezone).format(timeFormat)}
                        </>
                    );
                },
            }),
            columnHelper.accessor("completed", {
                enableSorting: false,
                header: t("completed"),
                cell: (info) => {
                    const completed = info.getValue();
                    return (
                        <span
                            className="tw-px-4 tw-py-2 text-green tw-font-bold tw-text-center"
                            style={{ borderRadius: "10px", background: "#48bbc326" }}
                        >
                            {completed || 0}
                        </span>
                    );
                },
            }),
            columnHelper.accessor("failed", {
                enableSorting: false,
                header: t("failed"),
                cell: (info) => {
                    const failed = info.getValue();
                    return (
                        <span
                            className="tw-px-4 tw-py-2 text-danger tw-font-bold tw-text-center"
                            style={{ borderRadius: "10px", background: "#ff00001a" }}
                        >
                            {failed || 0}
                        </span>
                    );
                },
            }),
            columnHelper.accessor("totalTime", {
                enableSorting: false,
                header: t("duration"),
                cell: (info) => {
                    const duration = info.getValue();
                    return renderDurationAsFormat((duration / 1000) || 0);
                },
            }),
            columnHelper.accessor("status", {
                enableSorting: false,
                header: t("status"),
                cell: (info) => t(info.getValue()),
            }),
            columnHelper.display({
                id: "actions",
                header: t("actions"),
                cell: (info) => {
                    const data = info.row.original?.result || [];
                    const type = info.row.original?.type;
                    const status = info.row.original?.status;
                    const failed = info.row.original?.failed;

                    return status === "completed" && (failed > 0 || type === "import") && (
                        <div className="tw-flex tw-items-center tw-justify-center">
                            <Info
                                className="cursor-pointer text-primary"
                                size={19}
                                onClick={() => open({ type, data })}
                            />
                        </div>
                    )
                },
            }),
        ];
        return columns;
    }, [t, primaryLocation?.timezone, dateFormat, timeFormat, open]);
};
